<style lang="scss">
    @import 'scss/app';
</style>
<template>

    <!-- Application -->
    <div class="map-tour" id="app" :style="{ minHeight: `calc(100vh - ${globalHeaderHeight}px)` }">

        <!-- Loading -->
        <template v-if="localLoading">
            Loading...
        </template>

        <!-- Loaded -->
        <template v-else>

            <!-- Map Tour Inner -->
            <div class="map-tour__inner">

                <!-- Map -->
                <Map />
                
                <!-- Welcome Modal -->
                <transition name="fade">
                    <WelcomeModal v-if="showWelcomeModal" />
                </transition>

                <!-- Touring Modal -->
                <transition name="fade">
                    <TouringModal v-if="showTouringModal" />
                </transition>

                <!-- Start Button -->
                <transition name="fade">
                    <StartButton v-if="!touring" />
                </transition>
            </div>

            <!-- Navigation -->
            <transition name="fade">
                <Navigation v-if="showTouringModal" />
            </transition>
        </template>
    </div>
</template>

<script>
    import Vue from 'vue'
    import Map from './components/Map.vue'
    import { mapGetters, mapActions } from 'vuex'
    import Navigation from './components/Navigation.vue'
    import StartButton from './components/StartButton.vue'
    import WelcomeModal from './components/WelcomeModal.vue'
    import TouringModal from './components/TouringModal.vue'

    export default {

        components: {
            Map,
            Navigation,
            StartButton,
            TouringModal,
            WelcomeModal,
        },

        data() {
            return {

                // Ready
                localLoading: true,
            }
        },

        computed: {
            ...mapGetters({
                configuration: 'configuration',
                touring: 'touring',
                tourStep: 'tourStep'
            }),

            /*
             * Returns the height of the global header on the page
             *
             * @return {integer}
             */
            globalHeaderHeight() {
                const globalHeader = document.querySelector('.global-header')
                if (globalHeader) {
                    return globalHeader.clientHeight
                }

                return 0
            },

            /*
             * Returns whether or not to display the welcome modal
             *
             * @return {boolean}
             */
            showWelcomeModal() {
                return (this.touring && this.tourStep < 0)
            },

            /*
             * Returns whether or not to display the touring modal
             *
             * @return {boolean}
             */
            showTouringModal() {
                return (this.touring && this.tourStep >= 0)
            },
        },

        methods: {
            ...mapActions({
                decrementTourStep: 'decrementTourStep',
                incrementTourStep: 'incrementTourStep',
                setConfiguration: 'setConfiguration',
            }),

            /*
             * Initialise the configuration file
             *
             * @returns {void}
             */
            initialiseConfiguration() {

                // Retrieve Map Settings
                const jsonSettings = JSON.parse(document.querySelector('.map-tour-settings').innerHTML)

                // Retrieve Map Icons
                const jsonIcons = []
                document.querySelectorAll('.map-tour-icon').forEach((icon) => {
                    jsonIcons.push(JSON.parse(icon.innerHTML))
                })

                const descriptionRegex = /"description": (.*)/gm

                // Retrieve Map Steps
                const jsonSteps = []
                document.querySelectorAll('.map-tour-step').forEach((step) => {
                    
                    // Convert description to valid JSON (sanitising as TerminalFour cannot)
                    let descriptionRegexResults
                    while ((descriptionRegexResults = descriptionRegex.exec(step.innerHTML)) !== null) {

                        // This is necessary to avoid infinite loops with zero-width matches
                        if (descriptionRegexResults.index === descriptionRegex.lastIndex) {
                            descriptionRegex.lastIndex++;
                        }
                        
                        // Determine if the regex results equals two
                        if (descriptionRegexResults.length == 2) {
                            
                            step.innerHTML = step.innerHTML.replace(descriptionRegexResults[1], (description) => {                                
                                description = description.replaceAll(/(\\n)/gm, "")
                                description = description.replaceAll(/^"|"$/gm, "")
                                description = JSON.stringify(description)
                                return description
                            })
                        }
                    }

                    // Initialise empty variables
                    let jsonStep = JSON.parse(step.innerHTML)
                    let jsonBoundary = null
                    let jsonAnimatedLines = []
                    let jsonMarkers = []

                    // Retrieve markers
                    document.querySelectorAll(`.map-tour-step-marker[data-step-id="${jsonStep.id}"]`).forEach((stepMarker) => {

                        // Parse marker as JSON
                        const jsonMarker = JSON.parse(stepMarker.innerHTML)

                        // Determine if the marker has a modal
                        if (stepMarker.dataset.modal == "true") {

                            // Set the modal information
                            jsonStep.modal.marker = jsonMarker
                        } else {

                            // Push to the markers array
                            jsonMarkers.push(jsonMarker)
                        }
                    })

                    // Retrieve animated lines
                    document.querySelectorAll(`.map-tour-step-animated-line[data-step-id="${jsonStep.id}"]`).forEach((stepAnimatedLine) => {
                        jsonAnimatedLines.push(JSON.parse(stepAnimatedLine.innerHTML))
                    })

                    // Retrieve boundary
                    const boundary = document.querySelector(`.map-tour-step-boundary[data-step-id="${jsonStep.id}"]`)
                    if (boundary) {
                        jsonBoundary = JSON.parse(boundary.innerHTML)
                    }

                    // Push step
                    jsonSteps.push({
                        ...jsonStep,
                        markers: jsonMarkers,
                        animatedLines: jsonAnimatedLines,
                        boundary: jsonBoundary
                    })
                })

                // Set configuration
                const configuration = {
                    settings: jsonSettings,
                    icons: jsonIcons,
                    steps: jsonSteps
                }

                // Retrieve configuration and set local component data
                this.setConfiguration(configuration)

                // Wait for UI to update
                Vue.nextTick(() => {

                    // Set local loading to false
                    this.localLoading = false
                })
            }
        },

        mounted() {

            // Initialise the Configuration file
            this.initialiseConfiguration()
        }
    }
</script>